import React, { useState, Fragment, createRef, useRef, useEffect } from "react";
import moment from "moment";
import isKey from "utils/keyboard-events";
import cleanseObj from "utils/cleanse-object";
import Input from "../ui/Input";

const dateFormat = "MM-DD-YYYY";

const DateField = ({
  parentDate,
  selectFirst,
  selectLast,
  previous,
  next,
  parentSelect,
  selected,
  onDateChange,
  onError,
  disabled,
  onFocus
}) => {
  const [date, setDate] = useState([
    {
      type: "month",
      format: "MM",
      value: "",
      selected: false,
      length: 2
    },
    {
      type: "day",
      format: "DD",
      value: "",
      selected: false,
      length: 2
    },
    {
      type: "year",
      format: "YYYY",
      value: "",
      selected: false,
      length: 4
    }
  ]);

  const [errors, setErrors] = useState({
    invalidDate: null,
    incompleteDate: null
  });

  const refs = useRef(date.map(() => createRef()));
  // console.log('data', date);

  useEffect(() => {
    if (parentDate) {
      updateDate(parentDate, date);
    }
    // eslint-disable-next-line
  }, [parentDate]);

  useEffect(() => {
    const currentItemSelected = date.some(item => item.selected);
    if (!currentItemSelected) {
      if (selected) {
        if (selectFirst) {
          select(date[0].type);
          refs.current[0].current.focus();
        } else if (selectLast) {
          const lastIndex = date.length - 1;
          select(date[lastIndex].type);
          refs.current[lastIndex].current.focus();
        }
      }
    } else if (!selected) {
      setDate(date.map(item => ({ ...item, selected: false })));
    }
    // eslint-disable-next-line
  }, [selected]);

  const updateDate = (parentDate, date) => {
    setDate(
      date.map(item => {
        return { ...item, value: parentDate.format(item.format) };
      })
    );
  };

  const handleRemove = (e, type) => {
    let newDate = date;
    const selectedItemIndex = newDate.findIndex(item => item.type === type);
    const selectedItem = newDate[selectedItemIndex];
    if (!selectedItem.value) {
      if (selectedItemIndex) {
        const previousItemIndex = selectedItemIndex - 1;
        const previousItem = newDate[previousItemIndex];
        newDate = newDate.map(item => {
          if (item.type === previousItem.type) {
            return {
              ...item,
              selected: true,
              value:
                item.value && item.value.substring(0, item.value.length - 1)
            };
          }
          return { ...item, selected: false };
        });
        refs.current[selectedItemIndex - 1].current.focus();
      } else {
        if (previous) previous();
        newDate = newDate.map(item => ({ ...item, selected: false }));
      }
    } else {
      newDate = newDate.map(item => {
        if (item.type === selectedItem.type) {
          const newValue = item.value.substring(0, item.value.length - 1);
          if (!newValue && !selectedItemIndex) {
            onDateChange(null);
          }
          return {
            ...item,
            value: newValue
          };
        }
        return { ...item, selected: false };
      });
    }
    return setDate(newDate);
  };

  const handleAdd = (e, type) => {
    let newDate = date;
    const selectedItemIndex = newDate.findIndex(item => item.type === type);
    const selectedItem = newDate[selectedItemIndex];
    const newString = selectedItem.value
      ? selectedItem.value.concat(String.fromCharCode(e.keyCode || e.charCode))
      : String.fromCharCode(e.keyCode || e.charCode);
    if (newString.length < selectedItem.length + 1) {
      newDate = newDate.map(item => {
        if (item.type === type) {
          return {
            ...item,
            value: newString
          };
        }
        return item;
      });
      if (newString.length === selectedItem.length) {
        newDate = newDate.map(item => {
          if (item.type === type) {
            return {
              ...item,
              value: newString
            };
          }
          return item;
        });
        let nextItemIndex = selectedItemIndex + 1;
        if (nextItemIndex === date.length) {
          handleComplete(newDate);
        } else {
          let nextItem = date[nextItemIndex];
          while (
            nextItem.value.length === nextItem.length &&
            nextItemIndex < date.length - 1
          ) {
            nextItemIndex += 1;
            nextItem = date[nextItemIndex];
          }
          if (
            nextItemIndex === date.length - 1 &&
            nextItem.value.length === nextItem.length
          ) {
            handleComplete(newDate);
          } else {
            newDate = newDate.map(item => {
              if (item.type === nextItem.type)
                return { ...item, selected: true };
              return { ...item, selected: false };
            });
          }

          refs.current[nextItemIndex].current.focus();
        }
      }
    }
    setDate(newDate);
  };

  const handleComplete = newDate => {
    console.log("handle complete");
    if (validDate(newDate)) {
      const completedDate = newDate.map(item => item.value).join("-");
      onDateChange(moment(completedDate, dateFormat));
    }
    console.log("next", next);
    if (next) next();
  };

  const validDate = newDate => {
    const numOfCharacters = newDate.reduce(
      (acc, curr) => acc + curr.value.length,
      0
    );
    const expectedNumOfCharacters = newDate.reduce(
      (acc, curr) => acc + curr.length,
      0
    );
    if (numOfCharacters < expectedNumOfCharacters) {
      return handleErrors({ ...errors, incompleteDate: true });
    }
    const newErrors = { ...errors, incompleteDate: null };

    const formattedDate = newDate.map(item => item.value).join("-");
    const isValidDate = moment(formattedDate, dateFormat).isValid();
    if (!isValidDate) {
      return handleErrors({ ...newErrors, invalidDate: true });
    }
    return resetErrors();
  };

  const resetErrors = () => {
    onError();
    setErrors();
    return true;
  };

  const handleErrors = errors => {
    const newErrors = cleanseObj(errors);
    setErrors(errors);
    onError(newErrors);
    return false;
  };

  const handleKeyDown = type => e => {
    if (isKey(e, ["backspace", "delete"])) {
      return handleRemove(e, type);
    }
    if (isKey(e, ["space"])) {
      return false;
    }
    if (isKey(e, ["number"])) {
      return handleAdd(e, type);
    }
  };

  const select = type => {
    setDate(
      date.map(item => {
        if (item.type === type) return { ...item, selected: true };
        return { ...item, selected: false };
      })
    );
  };

  return (
    <div className="flex" onClick={parentSelect}>
      <Fragment>
        {date.map((item, index) => (
          <Fragment key={index}>
            {!!index && (
              <div className="pr-1 text-2xl text-gray-primary">/</div>
            )}
            <Input
              ref={refs.current[index]}
              onFocus={() => {
                select(item.type);
                parentSelect();
                onFocus();
              }}
              tabIndex={-1}
              onKeyDown={handleKeyDown(item.type)}
              widthFull={false}
              size={item.length + 1}
              placeholder={item.initialValue || item.format}
              value={item.value || ""}
              disabled={disabled}
            />
          </Fragment>
        ))}
      </Fragment>
    </div>
  );
};

DateField.defaultProps = {
  initialDate: moment(),
  onDateChange: () => {},
  onError: () => {},
  disabled: false
};

export default DateField;
